import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {BrandLogo} from '../../atoms/BrandLogo/BrandLogo';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';
import {ProductImage} from '../ProductImage/ProductImage';
// eslint-disable-next-line no-restricted-imports
import type {SponsoredContentSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';

export const MIN_PRODUCTS_PER_TEASER_CARD = 3;

export const TeaserCard = ({
  campaign,
  onCardClick,
  header,
  className,
}: {
  campaign: SponsoredContentSection;
  onCardClick: () => void;
  header: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={className}
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onCardClick();
      }}
    >
      <CardLink
        to={campaign.targetUrl.url}
        aria-label={campaign.brand?.name ?? campaign.headline}
        colour={campaign.color.hex}
      >
        <ProductsRow>
          {campaign.productImages
            .slice(0, MIN_PRODUCTS_PER_TEASER_CARD)
            .map((productImage, index) => (
              <StyledProductImage
                key={`${campaign.promotionTracking.promotionId}-${index}`}
                src={productImage.url}
                alt={productImage.description}
                widths={[120, 240, 480]}
                breakpoints={[{columns: 3}]}
                padded
              />
            ))}
        </ProductsRow>

        <CardRightPanel>
          {header}
          {campaign.brand?.deprecatedWhiteLogoImage ? (
            <StyledBrandLogo
              name={campaign.brand.name}
              image={campaign.brand.deprecatedWhiteLogoImage}
              maxHeight="24px"
              showTooltip={false}
            />
          ) : (
            <BrandName>{campaign.brand?.name ?? campaign.headline}</BrandName>
          )}
        </CardRightPanel>
      </CardLink>
    </div>
  );
};

const CardLink = styled(UnstyledLink)<{colour?: string}>`
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  background: ${({colour}) => colour ?? COLORS.Black};
  border-radius: 8px;
  overflow: hidden;
  min-height: 100px;
`;

const CardRightPanel = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  width: 42%;
  padding: 8px;
`;

const BrandName = styled.div`
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px20};
  font-weight: ${FONT_WEIGHTS.w700};
  line-height: 1;
  text-align: center;
  text-transform: uppercase;
`;

const ProductsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  width: 58%;
  padding: 8px;
  align-items: center;
`;

const StyledBrandLogo = styled(BrandLogo)`
  max-width: 100%;
  height: 34%;
  color: ${COLORS.White};
  display: flex;
  justify-content: center;
  padding-right: 8px;
`;

const StyledProductImage = styled(ProductImage)`
  background: ${COLORS.Grey4};
  border-radius: 4px;
  min-height: 84px;
`;
